import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { useUpdateMedication } from '../../../../../api/created/medication.ts';
import {
  getGetMedicationQueryKey,
  useCreateMedication,
  useGetMedication,
  useSetMedicationStatus,
} from '../../../../../api/generated/medication-controller/medication-controller';
import type { MedicationDto } from '../../../../../api/generated/model';
import { queryClient } from '../../../../../api/queryClient';

const DEFAULT_PAGE_SIZE = 20;

export function useMedicationTable() {
  const [page, setPage] = useState(0);
  const size = DEFAULT_PAGE_SIZE;
  const [search, setSearch] = useState('');

  const { data: medications = [], isLoading } = useGetMedication({
    includeInactive: true,
    search: search.trim() || undefined,
  });

  const invalidateMedications = () => queryClient.invalidateQueries({ queryKey: getGetMedicationQueryKey() });

  const setStatusMutation = useSetMedicationStatus({
    mutation: {
      onSuccess: () => {
        void invalidateMedications();
      },
      onError: () => {
        toast.error('Beim (De-)Aktivieren der Medikation ist ein Fehler aufgetreten');
      },
    },
  });

  const createMutation = useCreateMedication({
    mutation: {
      onSuccess: () => {
        void invalidateMedications();
        toast.success('Medikament erfolgreich erstellt');
      },
      onError: () => {
        toast.error('Beim Erstellen der Medikation ist ein Fehler aufgetreten');
      },
    },
  });

  const updateMutation = useUpdateMedication({
    mutation: {
      onSuccess: () => {
        void invalidateMedications();
        toast.success('Medikament erfolgreich bearbeitet');
      },
      onError: () => {
        toast.error('Beim Bearbeiten der Medikation ist ein Fehler aufgetreten');
      },
    },
  });

  const filteredMedications = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return medications;
    return medications.filter((med) => med.name?.toLowerCase().includes(term));
  }, [medications, search]);

  const totalCount = filteredMedications.length;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / size) : 0;

  const paginatedMedications = useMemo(() => {
    const start = page * size;
    return filteredMedications.slice(start, start + size);
  }, [filteredMedications, page, size]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleToggleActive = (medication: MedicationDto) => {
    if (!medication.id) return;
    setStatusMutation.mutate({
      id: medication.id,
      params: { isActive: !medication.isActive },
    });
  };

  const handleCreate = (name: string) => {
    createMutation.mutate({ data: { name } });
  };

  const handleUpdate = (id: string, name: string) => {
    updateMutation.mutate({ id, data: { name } });
  };

  return {
    medications: paginatedMedications,
    allMedications: medications,
    isLoading,
    page,
    totalPages,
    search,
    setSearch: handleSearchChange,
    setPage,
    handleToggleActive,
    handleCreate,
    handleUpdate,
    isTogglePending: setStatusMutation.isPending,
    isCreatePending: createMutation.isPending,
    isUpdatePending: updateMutation.isPending,
  };
}
